import { useIntl } from '@/intl';
import { styled } from 'styled-components';
import { StyledToggleGroupItems, ToggleGroup, Typography, tk } from '@sunwebgroup/ui-components';

interface FilterToggleGroupProps<T extends string, CountKey extends string> {
  filter: T;
  filters: {
    value: T;
    label: string;
    countKey: CountKey;
  }[];
  counts: Record<CountKey, number>;
  onFilterChange: (filter: T) => void;
}

export function FilterToggleGroup<T extends string, CountKey extends string>({
  filter,
  filters,
  counts,
  onFilterChange,
}: FilterToggleGroupProps<T, CountKey>) {
  const intl = useIntl();

  return (
    <ToggleGroupStyled
      type="single"
      color="neutral"
      onValueChange={(value) => onFilterChange(value as T)}
      value={filter}
    >
      {filters.map(({ value, label, countKey }) => (
        <ToggleGroupItemStyled key={value.toString()} value={value}>
          {intl.formatMessage({ id: label })}
          <ToggleGroupItemNumber>{counts[countKey]}</ToggleGroupItemNumber>
        </ToggleGroupItemStyled>
      ))}
    </ToggleGroupStyled>
  );
}

const ToggleGroupStyled = styled(ToggleGroup)`
  margin-bottom: 8px;
  position: relative;
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  scrollbar-width: none;
  -ms-overflow-style: none;
  display: flex;
  margin-right: -16px;
  margin-left: -16px;

  &::-webkit-scrollbar {
    display: none;
  }

  &:after {
    content: '';
    margin-left: 16px;
  }

  &:before {
    content: '';
    margin-right: 16px;
  }

  ${StyledToggleGroupItems} {
    flex-wrap: nowrap;
  }
`;

const ToggleGroupItemNumber = styled.span`
  ${Typography.modVariant('body-sm-bold')}
  padding: 1px 5px;
  margin-left: 6px;
  border-radius: ${tk('radius_rounded')};
`;

const ToggleGroupItemStyled = styled(ToggleGroup.Item)`
  display: flex;
  align-items: baseline;
  &[data-state='on'] ${ToggleGroupItemNumber} {
    background-color: ${tk('color_palette_grey_900')};
  }

  &[data-state='off'] ${ToggleGroupItemNumber} {
    background-color: ${tk('color_palette_grey_200')};
  }
`;
